import React, { useEffect, useRef } from 'react';

import { AppName } from 'src/global/globalConfig';
import useProject from 'src/shared/hooks/useProject';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import Lozenges from './Lozenges';

import styles from './HabitatAssayLozenges.module.scss';

export type HabitatAssayLozengeOption = {
    habitatName: string;
    assayName: string;
    value: string;
    provider: string;
    source: string;
};

type HabitatAssayLozengesProps = {
    selectedValue: string;
    options: HabitatAssayLozengeOption[];
    onChange: (value: string) => void;
    app: AppName;
    className?: string;
    showThirdParty?: boolean;
    showEdnaOnly?: boolean;
    autoReset?: boolean; // flag to control auto reset when third party options are hidden
};

const HabitatAssayLozenges = (props: HabitatAssayLozengesProps) => {
    const { selectedValue, options, onChange, app, className, showThirdParty = false, autoReset = true, showEdnaOnly = true } = props;
    const elementRef = useRef<HTMLDivElement | null>(null);
    const { currentProjectDetails } = useProject();
    const { setCurrentProjectHabitatAssayType } = useProjectFilters();

    // filter third party options
    const nmProvider = 'NatureMetrics';
    const thirdPartyFilteredOptions = showThirdParty ? options : options.filter(option => option.provider === nmProvider);

    // filter eDNA source options
    const filteredOptions = showEdnaOnly ? thirdPartyFilteredOptions.filter(option => option.source === 'eDNA') : thirdPartyFilteredOptions;

    // auto reset currentProjectHabitatAssayType value if options don't have the selectedValue
    // this can happen when third party options are hidden
    useEffect(() => {
        if (autoReset && filteredOptions.length > 0) {
            const isValidSelectedValue = filteredOptions.some(fo => fo.value === selectedValue);
            if (selectedValue && !isValidSelectedValue) {
                setCurrentProjectHabitatAssayType(filteredOptions[0].value || '');
            }
        }
    }, [filteredOptions]);

    const lozengeOptions = filteredOptions.map(entry => ({
        label: (
            <div className={styles.lozenge} data-testid={`${entry.value}-lozenge`}>
                <span className={styles.habitat}>{entry.habitatName}</span>
                <span className={styles.assay}>{entry.source === 'eDNA' ? `${entry.assayName} (${entry.source})` : entry.assayName}</span>
            </div>
        ),
        value: entry.value,
    }));

    const onLozengeChange = (value: string) => {
        const event = new CustomEvent('analytics', {
            detail: {
                category: 'habitat-assay-lozenge',
                action: 'habitat_assay_changed',
                app,
                habitatAssayType: value,
                project: currentProjectDetails?.projectName,
            },
            bubbles: true,
        });

        elementRef.current?.dispatchEvent(event);

        onChange(value);
    };

    return (
        <span ref={elementRef}>
            <Lozenges options={lozengeOptions} selectedValue={selectedValue} onChange={onLozengeChange} className={className} />
        </span>
    );
};

export default HabitatAssayLozenges;
